/**
 * InvitePage (U15) — the sub invite-claim leg of the sharing seam. Shows the
 * scope-package summary the token exposes (never the contractor's estimate or
 * client data), then creates the sub's company + owner account and binds the
 * grant. Continuation matches signup: owners set up TOTP at first sign-in.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { BRAND, PublicInviteClaimRequestSchema } from '@buildledger/shared';
import type { PublicInviteResponse } from '@buildledger/shared';

import { ApiError, getInvite, postClaim } from '../api';
import { getSignupsOpen } from '../join-api';
import { fmtDateOnly } from '../format';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; status: number; message: string }
  | { kind: 'ready'; invite: PublicInviteResponse };

export function InvitePage({ token }: { token: string }): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [companyName, setCompanyName] = useState('');
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<{ companyName: string } | null>(null);
  // Accepting creates a new company for the sub, so it follows the sign-up switch.
  const [signupsOpen, setSignupsOpen] = useState(true);
  useEffect(() => {
    void getSignupsOpen().then(setSignupsOpen);
  }, []);

  useEffect(() => {
    getInvite(token)
      .then((invite) => {
        setLoad({ kind: 'ready', invite });
        setEmail((prev) => (prev === '' ? invite.invitedEmail : prev));
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError)
          setLoad({ kind: 'error', status: err.status, message: err.message });
        else setLoad({ kind: 'error', status: 0, message: 'Could not load this invite.' });
      });
  }, [token]);

  const parsed = useMemo(
    () =>
      PublicInviteClaimRequestSchema.safeParse({
        company: { name: companyName.trim() },
        user: {
          ...(userName.trim() === '' ? {} : { name: userName.trim() }),
          email: email.trim(),
          password,
        },
      }),
    [companyName, userName, email, password],
  );

  if (load.kind === 'loading') {
    return (
      <div className="stage stage-center">
        <div className="spinner" aria-label="Loading invite" />
      </div>
    );
  }

  if (load.kind === 'error') {
    return (
      <div className="stage stage-center">
        <div className="card notice-card">
          <div className="brand-rule" />
          <h1>{load.status === 404 ? 'This invite link is not valid' : 'Something went wrong'}</h1>
          <p>
            {load.status === 404
              ? 'It may have been revoked or already claimed. Ask the contractor to re-send it.'
              : load.message}
          </p>
        </div>
      </div>
    );
  }

  const { invite } = load;

  if (claimed !== null) {
    return (
      <div className="stage stage-center">
        <div className="card notice-card" data-testid="claim-success">
          <div className="brand-rule" />
          <h1>Welcome to {BRAND.name}</h1>
          <p>
            <strong>{claimed.companyName}</strong> is set up and connected to{' '}
            {invite.inviterCompanyName}. The scope package “{invite.packageTitle}” is waiting in
            your sub inbox.
          </p>
          <p>
            Sign in to continue — you&apos;ll set up two-factor authentication on your first
            sign-in, then you can review the scope and submit your quote.
          </p>
          <a className="btn btn-primary" href="/login">
            Sign in to {BRAND.name}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="stage stage-center">
      <div className="card invite-card">
        <div className="brand-rule" />
        <p className="invite-kicker">Invitation to quote</p>
        <h1>
          {invite.inviterCompanyName} invited you to price “{invite.packageTitle}”
        </h1>

        <div className="invite-scope">
          <h3>Scope shared with you</h3>
          <p className="invite-scope-text">
            {invite.scopeDescription || 'See details after sign-in.'}
          </p>
          {invite.dueDate !== null && (
            <p className="invite-due">Quotes are due by {fmtDateOnly(invite.dueDate)}.</p>
          )}
          <p className="muted">
            You will only ever see the scope shared with you — never the contractor&apos;s estimate,
            pricing, or client details. Your quote shows them your price, never your costs.
          </p>
        </div>

        {invite.claimable && !signupsOpen ? (
          <div className="invite-form" data-testid="invite-signups-closed">
            <h3>Accounts can&apos;t be created yet</h3>
            <p className="muted">
              {invite.inviterCompanyName} invited you, but new {BRAND.name} accounts aren&apos;t
              open yet. Your invitation stays valid — open this link again once sign-ups open, or
              let {invite.inviterCompanyName} know. If your company already uses {BRAND.name}, they
              can invite it from their directory instead.
            </p>
          </div>
        ) : invite.claimable ? (
          <form
            className="invite-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!parsed.success || busy) return;
              setBusy(true);
              setError(null);
              postClaim(token, parsed.data)
                .then((res) => setClaimed({ companyName: res.company.name }))
                .catch((err: unknown) => {
                  setError(
                    err instanceof ApiError ? err.message : 'Something went wrong — try again.',
                  );
                })
                .finally(() => setBusy(false));
            }}
          >
            <h3>Create your workspace</h3>
            <label className="field">
              <span>Company name</span>
              <input
                value={companyName}
                data-testid="claim-company"
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Your company LLC"
              />
            </label>
            <label className="field">
              <span>Your name (optional)</span>
              <input
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                autoComplete="name"
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                value={email}
                data-testid="claim-email"
                type="email"
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Password (10+ characters)</span>
              <input
                value={password}
                data-testid="claim-password"
                type="password"
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error && <p className="form-error">{error}</p>}
            <button
              className="btn btn-primary btn-wide"
              type="submit"
              data-testid="claim-submit"
              disabled={!parsed.success || busy}
            >
              {busy ? 'Creating…' : 'Create account & view scope'}
            </button>
            <p className="muted invite-fineprint">
              Creating an account accepts this invite for your company. Two-factor authentication is
              required at first sign-in.
            </p>
          </form>
        ) : (
          <div className="invite-form">
            <p className="form-error">
              This invite has already been claimed or is no longer available. If that seems wrong,
              ask {invite.inviterCompanyName} to re-send it.
            </p>
            <a className="btn btn-ghost" href="/login">
              Sign in instead
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
